import {merge} from "lodash-es";
import theme from "theme/index.scss";

/**
 * Graph Options
 */
export const GRAPH_OPTIONS = {
  navigation: false,
  selection: {
    color: theme.graphCiscoBlue,
    labelColor: theme.labelBgColor
  },
  labels: {
    maxLength: 20
  },
  iconFontFamily: "Phosphor",
  hoverDelay: 0
};

export const RELATIONS_GRAPH_OPTIONS = merge({}, GRAPH_OPTIONS, {
  overview: false,
  selection: {
    color: theme.graphCiscoBlue,
    labelColor: theme.labelBgColor
  }
});

export const ATTACK_GRAPH_OPTIONS = merge({}, GRAPH_OPTIONS, {
  maxItemZoom: 4,
  minZoom: 0.1, // default 0.05 (range: 0.001 to 1)
  overview: false
});

/**
 * Graph Components
 */
export const GRAPH_THEME_OPTIONS = {
  LIGHT: {
    controlTheme: "light",
    backgroundColor: theme.backgroundLight
  },
  DARK: {
    controlTheme: "dark",
    backgroundColor: theme.backgroundDark
  }
};

export const TIMEBAR_THEME_OPTIONS = {
  LIGHT: {
    backgroundColor: theme.timebarBg,
    style: {
      color: theme.timebarStackColor
    }
  },
  DARK: {
    backgroundColor: theme.timebarBgDark,
    style: {
      color: theme.timebarStackColorDark
    },
    labels: {
      color: theme.timebarStackColorDark
    },
    sliders: {
      color: theme.sliderColorDark
    }
  }
};

const LABEL_STYLES = {
  default: {
    label: {
      color: theme.labelTextColor,
      backgroundColor: theme.labelBgColor
    }
  },
  dusk: {
    label: {
      color: theme.labelTextColorDark,
      backgroundColor: theme.labelBgColorDark
    }
  }
};

/**
 * General theme colors
 * @todo Need to set correct dark disposition colors & implement on nodes
 */
export const THEME_COLORS = {
  LIGHT: {
    malicious: theme.graphDispositionMalicious,
    suspicious: theme.graphDispositionSuspicious,
    clean: theme.graphDispositionClean,
    unknown: theme.graphDispositionUnknown,
    common: theme.graphDispositionCommon,
    asset: theme.graphAssetColor
  },
  DARK: {
    malicious: theme.graphDispositionMalicious,
    suspicious: theme.graphDispositionSuspicious,
    clean: theme.graphDispositionClean,
    unknown: theme.graphDispositionUnknownDark,
    common: theme.graphDispositionCommon,
    asset: theme.graphAssetColorDark
  }
};

/**
 * Default styling for all Nodes.
 */
const NODE_STYLES_COMMON = {
  size: 1,
  shape: {
    height: 50,
    width: 50
  },
  border: {
    lineStyle: "solid",
    width: 2,
    radius: 8
  },
  label: {
    center: false,
    fontSize: 14
  }
};

export const NODE_STYLES = {
  default: merge({}, NODE_STYLES_COMMON, LABEL_STYLES.default, {
    color: theme.nodeBgColor,
    border: {
      color: theme.graphDispositionUnknown
    },
    fontIcon: {
      color: theme.graphDispositionUnknown
    }
  }),
  dusk: merge({}, NODE_STYLES_COMMON, LABEL_STYLES.dusk, {
    color: theme.nodeBgColorDark,
    border: {
      color: theme.graphDispositionUnknownDark
    },
    fontIcon: {
      color: theme.graphDispositionUnknownDark
    }
  })
};

/**
 * Combo Styles
 */
const COMMON_COMBO_STYLES = {
  size: 1,
  arrange: "concentric",
  border: {
    width: 4
  },
  label: {
    center: false,
    fontSize: 14
  },
  glyphs: [
    {
      position: "ne",
      angle: 0,
      size: 1.5,
      label: {
        bold: true
      }
    }
  ],
  closedStyle: {
    border: {width: 4}
  }
};

export const COMBO_STYLES = {
  default: merge({}, COMMON_COMBO_STYLES, LABEL_STYLES.default, {
    color: theme.nodeBgColor,
    glyphs: [
      {
        color: theme.comboCountGlyphBgColor,
        label: {
          color: theme.comboCountGlyphTextColor,
          bold: true
        }
      }
    ],
    closedStyle: {
      color: theme.nodeBgColor
    }
  }),
  dusk: merge({}, COMMON_COMBO_STYLES, LABEL_STYLES.dusk, {
    color: theme.nodeBgColorDark,
    glyphs: [
      {
        color: theme.comboCountGlyphBgColorDark,
        label: {
          color: theme.comboCountGlyphTextColorDark,
          bold: true
        },
        border: {
          color: theme.comboCountGlyphTextColorDark,
          size: 1
        }
      }
    ],
    closedStyle: {
      color: theme.nodeBgColorDark
    }
  })
};

/**
 * Default styling for all Edges.
 * @todo Dark theme?
 */
export const EDGE_STYLE_COMMON = {
  width: 2,
  color: theme.defaultNodeIconColor,
  label: {
    fontSize: 14
  },
  end2: {
    arrow: true
  }
};

export const EDGE_STYLES = {
  default: merge({}, EDGE_STYLE_COMMON, LABEL_STYLES.default),
  dusk: merge({}, EDGE_STYLE_COMMON, LABEL_STYLES.dusk)
};

export const PING_OPTIONS = {
  color: theme.graphCiscoBlue,
  haloRadius: 60,
  haloWidth: 60,
  time: 400,
  repeat: 1
};

/**
 * Layouts
 */
export const LAYOUT_OPTIONS = [
  {name: "Lens", value: "lens", selected: true},
  {name: "Organic", value: "organic", selected: false},
  {name: "Structural", value: "structural", selected: false},
  {name: "Radial", value: "radial", selected: false},
  {name: "Sequential", value: "sequential", selected: false}
];

export const LAYOUTS = {
  lens: {name: "lens"},
  organic: {name: "organic"},
  structural: {name: "structural"},
  radial: {name: "radial"},
  sequential: {name: "sequential", curvedLinks: true}
};

export const DEFAULT_ATTACK_GRAPH_LAYOUT = {
  name: "sequential",
  curvedLinks: true,
  orientation: "right", // default orientation after Rob Gresham audit 3/20
  packing: "aligned",
  stacking: {arrange: "grid"},
  stretch: 1, // @todo: experiment with this for improved attack graph UI
  tightness: 5
};

export const SEQUENTIAL_LAYOUT_ORIENTATIONS = [
  {name: "Down", value: "down", selected: false},
  {name: "Up", value: "up", selected: false},
  {name: "Right", value: "right", selected: false},
  {name: "Left", value: "left", selected: false}
];

/** Default Combine */
export const COMBINE_FIELDS = {
  observableOfTarget: "observable_of_target",
  targetGroup: "target_group",
  compaction: "compaction",
  simplification: "simplification"
};

export const DEFAULT_COMBINE_ON = {
  level: 4,
  properties: Object.values(COMBINE_FIELDS)
};
